import type { Activity, Agent, Message } from '../api/types'

export type ActivityLabel = 'asked for a permission' | 'working' | 'idle' | 'not running'

/**
 * What a running agent is doing, in the words the profile, the roster and the
 * sidebar share. "Asked for a permission" is in the past tense because no hook
 * says when a permission prompt was answered, so the state holds until the
 * turn ends. A running agent from a server that does not say is idle.
 */
export function activityLabel(online: boolean, activity: Activity | null | undefined): ActivityLabel {
  if (!online) return 'not running'
  if (activity === 'blocked') return 'asked for a permission'
  return activity === 'working' ? 'working' : 'idle'
}

export interface OnIt {
  agent: Agent
  activity: 'working' | 'blocked'
}

/**
 * The agents on a person's message in `view`, where a chat app draws "typing":
 * each one working or blocked that holds a `received` on it and has said
 * nothing since, in `held`, every message the page holds, so an answer in a
 * thread the room does not show counts. A receipt and a message are sent
 * under the session's live branch, which may not be its name in the roster.
 */
export function onItOf(view: Message[], held: Message[], roster: Agent[]): OnIt[] {
  const onIt: OnIt[] = []
  for (const agent of roster) {
    const activity = agent.online ? agent.activity : null
    if (activity !== 'working' && activity !== 'blocked') continue
    const isAgent = (name: string) => name === agent.name || name === agent.branch
    const lastSaid = Math.max(-Infinity, ...held
      .filter((message) => message.sender_type === 'agent' && isAgent(message.sender))
      .map((message) => Date.parse(message.posted_at)))
    const waiting = view.some((message) => message.sender_type === 'human'
      && Date.parse(message.posted_at) > lastSaid
      && message.reactions.some((reaction) => reaction.reaction === 'received' && reaction.actors.some(isAgent)))
    if (waiting) onIt.push({ agent, activity })
  }
  return onIt
}
